// InlineInput.tsx
import React, { useState, useRef, useEffect, forwardRef } from 'react';
import styled from 'styled-components';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';

const InputWrapper = styled.div`
    display: inline-block;
    vertical-align: baseline;
    margin: 0 2px;
`;

const HiddenSpan = styled.span`
    visibility: hidden;
    position: absolute;
    white-space: pre;
    padding: 0 2px;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
`;

const StyledInput = styled.input<{ $colorMode?: 'light' | 'dark'; $minWidth?: string; $maxWidth?: string; $includeStyling?: boolean }>`
    display: inline-block;
    border-radius: 8px;
    background-color: ${Colors.transparent};
    // background-color: ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.grey8+60 : Colors.grey2+60) : Colors.transparent};
    border: none;
    outline: none;
    padding: 4px 8px;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: inherit;
    text-align: left;
    line-height: inherit;
    color: ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.grey1 : Colors.grey9) : (props.$colorMode === 'dark' ? Colors.grey1 : Colors.grey9)};
    gap: 10px;
    width: auto;
    min-width: ${props => props.$minWidth || '10px'};
    max-width: ${props => props.$maxWidth};
    box-sizing: content-box;
    cursor: text;
    border: 1px solid ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey3) : Colors.transparent};
    transition: border 0.2s ease-in-out;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;

    &:hover {
        background-color: ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.black : Colors.white) : Colors.transparent};
        border: 1px solid ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.grey5 : Colors.grey5) : Colors.transparent};
    }

    &:focus {
        background-color: ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.black : Colors.white) : Colors.transparent};
        border: 1px solid ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.grey4 : Colors.grey6) : Colors.transparent};
    }

    &:disabled {
        border: 1px solid ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.grey5 : Colors.grey5) : Colors.transparent};
        background-color: ${props => props.$includeStyling ? (props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey2) : Colors.transparent};
        color: ${Colors.grey5};
        cursor: pointer;
    }
`;

interface InlineInputProps {
    value?: string | number;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    disabled?: boolean;
    colorMode?: 'light' | 'dark';
    onEnterPress?: () => void;
    minWidth?: string;
    maxWidth?: string;
    includeStyling?: boolean;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const InlineInput = forwardRef<HTMLInputElement, InlineInputProps>(({
    value: initialValue = '',
    onChange,
    placeholder,
    disabled = false,
    colorMode = 'light',
    onEnterPress,
    minWidth,
    maxWidth = '100%',
    includeStyling = false,
    onBlur,
}, ref) => {
    const [value, setValue] = useState(initialValue.toString());
    const inputRef = useRef<HTMLInputElement>(null);
    const spanRef = useRef<HTMLSpanElement>(null);

    // Update internal state when initialValue prop changes
    useEffect(() => {
        setValue(initialValue.toString());
    }, [initialValue]); // Only re-run if initialValue changes

    useEffect(() => {
        if (inputRef.current && spanRef.current) {
            const calculatedWidth = spanRef.current.offsetWidth;

            if (maxWidth && calculatedWidth > parseInt(maxWidth)) {
                inputRef.current.style.width = maxWidth;
            } else {
                inputRef.current.style.width = `${calculatedWidth}px`;
            }
        }
    }, [value, maxWidth]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        onChange && onChange(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && onEnterPress) {
            e.preventDefault();
            onEnterPress();
            inputRef.current?.blur();
        }
    };

    return (
        <InputWrapper>
            <HiddenSpan ref={spanRef}>
                {value || placeholder || ''}
            </HiddenSpan>
            <StyledInput
                ref={(element) => {
                    inputRef.current = element;
                    if (typeof ref === 'function') {
                        ref(element);
                    } else if (ref) {
                        ref.current = element;
                    }
                }}
                type="text"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={onBlur}
                placeholder={placeholder}
                disabled={disabled}
                $colorMode={colorMode}
                $minWidth={minWidth}
                $maxWidth={maxWidth}
                $includeStyling={includeStyling}
            />
        </InputWrapper>
    );
});

export default InlineInput;